/**
 * A person's page, previewed beside the page you're on (opened from a person card, e.g. the meeting's People tab):
 * who they are, their leaning, the profile's ledger, latest positions and appearances, and a button to the full
 * profile. The data is the profile's own (peopleStore.loadProfile, GET /api/dashboard/people/<id>/profile); the
 * privacy rules are the Worker's, as on the profile: a person you may not see shows only what the card showed.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { navigate, useLocationKey } from "../app/router";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { prefersReducedMotion, reducedFallback, recipes } from "../motion";
import { peopleStore, usePeople } from "./peopleStore";
import { LeaningMeter } from "./LeaningMeter";
import { PersonImg, StanceChip, Watch, mdy, profileHref } from "./atoms";
import type { PersonProfile } from "./model";
import { getViewer, personHref } from "../app/viewer";

/** What the card already knows, shown at once while the profile loads (or if it can't be shown). */
export interface PreviewSeed { id: string; name: string; ward?: number; photoUrl?: string | null; sub?: string }

let open: ((seed: PreviewSeed) => void) | null = null;
/** Open the preview for a person (from any card). */
export const previewPerson = (seed: PreviewSeed) => {
  // The public meeting page has no preview (it reads dashboard profiles): open the person's public page, if any.
  if (getViewer().publicPage) { const href = personHref(seed.id); if (href) location.href = href; return; }
  open?.(seed);
};

export function PersonPreviewHost() {
  const [seed, setSeed] = useState<PreviewSeed | null>(null);
  const where = useLocationKey();
  useEffect(() => { open = setSeed; return () => { if (open === setSeed) open = null; }; }, []);
  useEffect(() => setSeed(null), [where]);   // following a link inside it (an appearance) leaves the page: close
  return seed ? <PersonPreview key={seed.id} seed={seed} onClose={() => setSeed(null)} /> : null;
}

function PersonPreview({ seed, onClose }: { seed: PreviewSeed; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const cached = usePeople((s) => s.profiles[seed.id]);
  const [state, setState] = useState<"loading" | "ready" | "none">(cached && !("error" in cached) ? "ready" : "loading");
  const p = cached && !("error" in cached) ? (cached as PersonProfile) : null;
  useFocusTrap(ref, true, { onEscape: onClose });
  useEffect(() => {
    if (p) return;
    let dead = false;
    void peopleStore.getState().loadProfile(seed.id).then((x) => { if (!dead) setState(x ? "ready" : "none"); });
    return () => { dead = true; };
  }, [seed.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: "translateX(24px)" }, { opacity: 1, transform: "none" }],
      { duration: prefersReducedMotion() ? reducedFallback.duration : recipes.peek.duration, easing: recipes.peek.easing });
  }, []);
  const openProfile = () => { onClose(); navigate(profileHref(seed.id)); };
  const name = p?.name ?? seed.name;
  const sub = p?.role ?? seed.sub ?? (seed.ward != null ? `Ward ${seed.ward}` : "");
  return (
    <div className="pp-layer" onClick={onClose}>
      <aside ref={ref} className="pp" role="dialog" aria-modal="true" aria-label={name} onClick={(e) => e.stopPropagation()} aria-busy={state === "loading" || undefined}>
        <header className="pp__head">
          {p ? <PersonImg image={p.image} name={name} size={56} w={72} h={48} />
            : <span className="pp__face">{seed.photoUrl ? <img src={seed.photoUrl} alt="" /> : name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}</span>}
          <span className="pp__who"><b>{name}</b>{sub && <small>{sub}</small>}</span>
          <button type="button" className="icon-btn pp__close" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </header>
        <div className="pp__body">
          {p?.leaning && <LeaningMeter estimate={p.leaning} size="mini" />}
          {p && (
            <div className="pp__ledger">
              <span><b>{p.stats.meetings}</b><small>Meetings spoken at</small></span>
              <span><b>{p.stats.moments}</b><small>Moments on record</small></span>
              <span><b>{p.stats.positions}</b><small>Positions taken</small></span>
            </div>
          )}
          {p && p.positions.length > 0 && (
            <section className="pp__section">
              <h3>Positions taken</h3>
              <ul>{p.positions.slice(0, 3).map((x) => (
                <li key={x.id}><span className="pp__line">{x.label}</span><StanceChip stance={x.stance} /></li>
              ))}</ul>
            </section>
          )}
          {p && p.appearancesList.length > 0 && (
            <section className="pp__section">
              <h3>Appearances</h3>
              <ul>{p.appearancesList.slice(0, 3).map((a) => (
                <li key={a.id}><span className="pp__line"><b>{a.meetingLabel}</b> · {mdy(a.date)}<br />{a.summary}</span><Watch meetingId={a.meetingId} at={a.from} /></li>
              ))}</ul>
            </section>
          )}
          {state === "loading" && <p className="pp__loading" aria-hidden><span /><span /><span /></p>}
        </div>
        <footer className="pp__foot">
          <button type="button" className="btn btn--primary" onClick={openProfile}>Open profile<ArrowRight size={14} aria-hidden /></button>
        </footer>
      </aside>
    </div>
  );
}
